<template>
  <div>
    <div class="infoWindowHeader">
      <InfoWindowName
        v-if="localContent.title || editing"
        :info-window-name="localContent.title"
        placeholder="Portfolio Name"
        :editing="editing"
        data-testid="infoWindowName"
        @updateName="(name: string) => localContent.updateName(name)"
      />
    </div>
    <div class="infoWindowBody">
      <DisplayFields
        :display-fields="localContent.displayFields"
        :editing="editing"
        data-testid="portfolioDisplayFields"
        @addDisplayField="(newField: DisplayField) => localContent.addDisplayField(newField)"
        @updateDisplayField="(newField: DisplayField, updatedField: DisplayField) => localContent.updateDisplayField(newField, updatedField)"
        @deleteDisplayField="(oldField: DisplayField) => localContent.deleteDisplayField(oldField)"
      />
      <div v-if="overlays.length">
        <div class="spacer" />
        <hr>
      </div>
      <v-progress-circular
        v-if="isLoadingOverlays"
        indeterminate
      />
      <OverlayRows
        v-if="overlays.length"
        data-testid="portfolioOverlayRows"
        :overlays="overlays"
      />
      <LinkedDocuments
        :editing="editing"
        :is-linking-documents="isLinkingDocuments"
        :info-window-content="localContent"
        :icon-url="iconUrl!"
        section-title="Buildings"
        :config="linkingConfig"
        placeholder-text="Enter Building Name"
        @stop-linking="isLinkingDocuments = false"
      />
      <v-container v-if="editing && localContent.id !== newBuildingId">
        <v-row
          v-if="!isLinkingDocuments"
          style="justify-content: end;"
        >
          <v-btn
            data-testid="link-building-button"
            prepend-icon="mdi-link-plus"
            class="mb-2"
            color="primary"
            variant="outlined"
            @click="isLinkingDocuments = true"
          >
            Building
          </v-btn>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<script lang="ts">
// Components
import InfoWindowName from './sections/Header/InfoWindowName.vue';
import DisplayFields from './DisplayFields/DisplayFields.vue';
import OverlayRows from './DisplayFields/OverlayRows/OverlayRows.vue';
import LinkedDocuments from './DisplayFields/LinkedDocuments/LinkedDocuments.vue';

// Tools & Services
import {mapMutations, mapActions} from 'vuex';
import {getCollection} from '@/lib/filters/filters.js';
import {currentUser, updateDocument} from '@/services/FirestoreService';
import {createCompetitivePortfolioContent, InfoWindowContent} from '@/lib/classes/InfoWindowContent';
// eslint doesn't recognize using a type in the template tags
// eslint-disable-next-line no-unused-vars
import type {DisplayField} from '@/lib/classes/InfoWindowContent';
import {gmAPI, Markers} from '@/services/MapService';
import {
  competitiveBuildingToJSON,
  duplicateComp,
  getAuditTrailToolbarBtn,
  getUnderwritingDashboardToolbarBtn,
  isEmptyBuilding,
  hasUnderwritingData,
} from '@/lib/classes/InfoWindowContent/helpers/competitive';
import {FlagService, FLAGS} from '@/services/FlagService';
import {NEW_BUILDING_ID} from '@/lib/symbols';
import type {Overlay} from '@/services/OverlaysService';
import {markerIconMap} from '@/services/MapService/Icons';
import {LinkingConfig, createLinkingConfig} from './DisplayFields/LinkedDocuments/Linking.js';
import {formatWithCommas} from '@/lib/ReusableFunctions/Formatters';
import {panToMarker} from '@/lib/map/panToMarker';
import {wrapError} from '@northpoint-development/labs-common-core';
import {
  reflectPortfolioBuildingDeletion,
  reflectPortfolioBuildingUndoDeletion,
} from '@/lib/underwriting/adapters/scenario/syncDeletions';

export default {
  name: 'CompetitivePortfolioInfoWindow',
  components: {
    InfoWindowName,
    DisplayFields,
    OverlayRows,
    LinkedDocuments,
  },
  props: {
    infoWindowContent: {
      type: InfoWindowContent,
      required: true,
    },
  },
  emits: ['setInfoWindowToolbar'],
  data(): {
    editing: boolean;
    localContent: InfoWindowContent;
    isLinkingDocuments: boolean;
    iconUrl: string|undefined;
    isLoadingOverlays: boolean;
    overlays: Overlay[];
    linkingConfig: LinkingConfig;
    newBuildingId: symbol;
    isOm: boolean;
    isBulk: boolean;
    isStaged: boolean;
  } {
    const iconUrl = markerIconMap.get('Blue-Balloon-Flat')?.url;
    const isOm = this.infoWindowContent.type === 'omCompetitivePortfolio';
    const isBulk = this.infoWindowContent.type === 'bulkCompetitivePortfolio';
    return {
      editing: false,
      localContent: this.infoWindowContent,
      isLinkingDocuments: false,
      iconUrl,
      isLoadingOverlays: false,
      overlays: [],
      linkingConfig: createLinkingConfig({
        firestoreCollection: 'beyondMaps-properties',
        filterCollection: 'competitiveBuildings',
        isOmInfoWindow: isOm || isBulk,
        fieldsToDisplay: ['Address', 'Clear Height', 'Year Built', 'Building Size'],
        iwAction: this.createCompetitiveBuildingTab,
        linkAction: panToMarker,
        sectionHeaderSuffixField: 'Building Size',
        transformSuffix: (value) => value ? `${formatWithCommas(value)} SF`: '',
        onUnlink: this.infoWindowContent.metadata?.generatedByOm
          ? (buildingId) => reflectPortfolioBuildingDeletion(buildingId, this.localContent.id)
              .catch((err) => console.error(wrapError(err, 'Error reflecting building unlink in underwriting scenarios')))
          : undefined,
        onUndoUnlink: this.infoWindowContent.metadata?.generatedByOm
          ? (buildingId) => reflectPortfolioBuildingUndoDeletion(buildingId, this.localContent.id)
              .catch((err) => console.error(wrapError(err, 'Error reflecting building unlink undo in underwriting scenarios')))
          : undefined,
      }),
      newBuildingId: NEW_BUILDING_ID,
      isOm,
      isBulk,
      // Centralized on InfoWindowContent so the view doesn't re-derive staged-ness from the type.
      isStaged: this.infoWindowContent.isStaged,
    };
  },
  computed: {
    isNew() {
      return this.localContent.id === NEW_BUILDING_ID;
    },
  },
  watch: {
    // watch localContent.id to see when the portfolio is first saved.
    // When it is saved, set a local flag to delete the temp marker and add the portfolio to the collection on unmount
    'localContent.id': {
      handler(newId, oldId) {
        if (newId !== NEW_BUILDING_ID && oldId === NEW_BUILDING_ID) {
          // Highlight does not persist, possibly related to [this issue](https://github.com/visgl/deck.gl/issues/8936)
          Markers.setHighlight('competitivePortfolios', newId, true);
          // when a new portfolio is saved, the highlight and close actions need to use the newly generated id
          if (this.localContent.host) {
            this.localContent.host.setHighlight = (state) => Markers.setHighlight('competitivePortfolios', newId, state);
            this.localContent.host.closeActions = () => Markers.setDraggable('competitivePortfolios', newId, false);
          }
        }
      },
    },
  },
  async mounted() {
    this.isLoadingOverlays = true;
    this.overlays = await this.localContent.overlays ?? [];
    this.isLoadingOverlays = false;

    if (this.isBulk) {
      this.enterEditMode();
    } else if (this.isNew || this.isOm) {
      this.enterEditMode();
      // Save the new portfolio immediately so that it has an id
      await this.localContent.updateSourceData();
      this.convertNewPortfolio();
    } else {
      this.enterViewMode();
    }
  },
  beforeUnmount() {
    if (!this.isStaged && this.editing &&
      isEmptyBuilding('competitivePortfolios', this.localContent)) {
      this.deletePortfolio({deleteMessage: 'New portfolio has been cancelled'});
    }
  },
  methods: {
    ...mapMutations(['setSnack']),
    ...mapMutations('infowindow', ['updateInfoWindowContents']),
    ...mapActions('infowindow', ['closeInfoWindowTab', 'createCompetitiveBuildingTab', 'createCompetitivePortfolioTab', 'closeInfoWindow']),
    enterViewMode() {
      const toolbarOptions = [];
      toolbarOptions.push([getAuditTrailToolbarBtn(this.localContent.id, 'competitivePortfolios')]);
      if (FlagService?.getBooleanFlag(FLAGS.UNDERWRITING_DASHBOARD_ENABLED) &&
        hasUnderwritingData(this.localContent.metadata)) {
        toolbarOptions.push([getUnderwritingDashboardToolbarBtn('portfolio', this.localContent.id)]);
      }
      toolbarOptions.push([{
        icon: 'ContentCopy',
        name: 'Duplicate Comp',
        action: 'duplicateMarker',
        callback: () => this.duplicateMarker(),
      }]);
      toolbarOptions.push([{
        icon: 'Pencil',
        name: 'Edit Comp',
        action: 'enterEditMode',
        callback: () => this.enterEditMode(),
      }]);
      this.$emit('setInfoWindowToolbar', {toolbarOptions});
      this.editing = false;
      this.toggleDrag(false);
    },
    enterEditMode() {
      let toolbarOptions;
      if (this.localContent?.host?.getEditModeToolbar) {
        toolbarOptions = this.localContent.host.getEditModeToolbar();
      } else {
        toolbarOptions = [];
        toolbarOptions.push([getAuditTrailToolbarBtn(this.localContent.id, 'competitivePortfolios')]);
        toolbarOptions.push([{
          icon: 'ContentCopy',
          name: 'Duplicate Comp',
          action: 'duplicateMarker',
          callback: () => this.duplicateMarker(),
        }]);
        toolbarOptions.push([{
          icon: 'Trash',
          name: 'Delete Comp',
          action: 'delete',
          callback: () => {
            this.deletePortfolio();
          },
        }]);
        toolbarOptions[2].push({
          icon: 'Check',
          name: 'Finish Editing',
          action: 'enterViewMode',
          callback: async () => {
            if (isEmptyBuilding('competitivePortfolios', this.localContent)) {
              await this.deletePortfolio({deleteMessage: 'New portfolio has been cancelled'});
              return;
            }
            if (this.isLinkingDocuments) {
              this.isLinkingDocuments = false;
            }
            this.enterViewMode();
          },
        });
      }
      this.$emit('setInfoWindowToolbar', {toolbarOptions});
      this.editing = true;
      this.toggleDrag(true);
    },
    async duplicateMarker() {
      const newMarkerContent = await duplicateComp(this.localContent, 'competitivePortfolios', createCompetitivePortfolioContent);
      this.editing = false;
      this.closeInfoWindow();
      this.updateInfoWindowContents({content: newMarkerContent});
    },
    toggleDrag(canDrag: boolean) {
      if (this.isNew) return;
      Markers.setDraggable(`${this.localContent.type}s`, this.localContent.id, canDrag, (latLng: google.maps.LatLng) => {
        this.localContent.location = new gmAPI.LatLng(latLng);
      });
    },
    async deletePortfolio(options?: {deleteMessage?: string}) {
      const docId = this.localContent.id as string;
      const localCollectionData = getCollection('competitivePortfolios').getItem(docId);
      // The tab must be closed after retrieving the collection data for undo, because doing so removes the item
      // from the collection
      const iWTabIndex = await this.closeInfoWindowTab(this.localContent.id);

      getCollection('competitivePortfolios')?.removeItems({id: docId});
      const date = new Date();
      const user = currentUser();
      await updateDocument('beyondMaps-portfolios', docId, {
        deleted: date,
        editedOn: {
          timestamp: date,
          name: user!.displayName,
          uid: user!.uid,
        },
      });

      this.setSnack({
        message: options?.deleteMessage ? options.deleteMessage : 'Portfolio has been deleted',
        action: {
          text: 'Undo',
          callback: () => this.undoDelete(localCollectionData, iWTabIndex),
        },
      });
    },
    async undoDelete(restoreData: any, iWTabIndex: number) {
      try {
        const date = new Date();
        const user = currentUser();
        await updateDocument('beyondMaps-portfolios', this.localContent.id as string, {
          deleted: null,
          editedOn: {
            timestamp: date,
            name: user!.displayName,
            uid: user!.uid,
          },
        });
        if (!restoreData || !restoreData.id) {
          throw new Error('No data to restore');
        }
        getCollection('competitivePortfolios').addItems(restoreData);

        const iwContent = competitiveBuildingToJSON(this.localContent) as any;
        iwContent.editedOn = {
          timestamp: date,
          name: user!.displayName,
          uid: user!.uid,
        };
        iwContent.geohash = this.localContent.metadata?.geohash;
        iwContent.markerData = {
          collection: 'competitivePortfolios',
          id: this.localContent.id as string,
          icon: iwContent.generatedByOm ? 'Darker-Blue-Portfolio' : 'Blue-Portfolio-Flat',
        };

        await this.createCompetitivePortfolioTab({
          subject: {
            ...iwContent,
            firestoreCollection: 'beyondMaps-portfolios',
            filterCollection: 'competitivePortfolios',
          },
          insertIndex: iWTabIndex,
        });
      } catch (err) {
        console.error(wrapError(err, 'Error undoing delete of competitive portfolio'));
      }
    },
    convertNewPortfolio() {
      // if this was a new portfolio when the window was opened, but has now been saved to the db,
      // the portfolio should be added to the collection to ensure the marker is persisted.
      getCollection('competitivePortfolios').removeItems({id: NEW_BUILDING_ID});
      Markers.delete('competitivePortfolios', NEW_BUILDING_ID);
    },
  },
};
</script>

<style scoped lang="scss">
.spacer {
  margin-top: 5px;
  padding-bottom: 15px;
}
.infoWindowBody {
  max-width: 400px;
}
</style>
